/**
 * EntityDetailForm - Form component for entity creation/editing
 * Handles all form fields, validation, image upload, and location picker
 * @idempotent @atomic
 */
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from '../../../services/toastService'
import { uploadAPI } from '../../../services/api'
import { createLogger } from '../../../utils/logger'
import BasicInfoFields from './form/BasicInfoFields'
import ContactInfoFields from './form/ContactInfoFields'
import LocationFields from './form/LocationFields'
import ImageUploadSection from './form/ImageUploadSection'
import { 
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  TruckIcon
} from '@heroicons/react/24/outline'

const logger = createLogger('EntityDetailForm')

const EntityDetailForm = memo(function EntityDetailForm({
  formData,
  onChange,
  validationErrors,
  editing,
  isEditMode
}) {
  const { t } = useTranslation()
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [mapPosition, setMapPosition] = useState(
    formData.currentLatitude && formData.currentLongitude
      ? { lat: formData.currentLatitude, lng: formData.currentLongitude }
      : null
  )

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`${file.name} is too large. Max 5MB per image.`)
      return
    }
    
    setUploadingImage(true)
    try {
      const uploadResponse = await uploadAPI.uploadImage(file)
      onChange({ ...formData, imageUrl: uploadResponse.data.url })
      toast.success('Image uploaded successfully')
    } catch (error) {
      logger.error('Failed to upload image', error)
      toast.error('Failed to upload image')
    } finally {
      setUploadingImage(false)
    }
  }

  const handleLocationSelect = (newPosition) => {
    if (newPosition) {
      setMapPosition(newPosition)
      onChange({
        ...formData,
        currentLatitude: newPosition.lat,
        currentLongitude: newPosition.lng
      })
    }
  }

  const handleImageRemove = () => {
    onChange({ ...formData, imageUrl: '' })
  }

  const handleInputChange = (field, value) => {
    onChange({ ...formData, [field]: value })
  }

  if (!editing) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Image Display */}
          {formData.imageUrl && (
            <div className="md:col-span-2">
              <img
                src={formData.imageUrl}
                alt={formData.name}
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          )}

          {/* Basic Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <TruckIcon className="h-4 w-4 inline mr-1" />
              Name
            </label>
            <div className="text-gray-900">{formData.name || 'N/A'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <div className="text-gray-900 capitalize">{formData.entityType?.replace('_', ' ') || 'N/A'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <div className="text-gray-900">{formData.category || 'N/A'}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <div className={`inline-block px-2 py-1 rounded text-sm ${formData.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
              {formData.isActive ? 'Active' : 'Inactive'}
            </div>
          </div>

          {/* Description */}
          {formData.description && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <div className="text-gray-900 whitespace-pre-wrap">{formData.description}</div>
            </div>
          )}

          {/* Contact Info */}
          {formData.email && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <EnvelopeIcon className="h-4 w-4 inline mr-1" />
                Email
              </label>
              <a href={`mailto:${formData.email}`} className="text-primary hover:underline">
                {formData.email}
              </a>
            </div>
          )}

          {formData.phone && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <PhoneIcon className="h-4 w-4 inline mr-1" />
                Phone
              </label>
              <a href={`tel:${formData.phone}`} className="text-primary hover:underline">
                {formData.phone}
              </a>
            </div>
          )}

          {formData.website && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <GlobeAltIcon className="h-4 w-4 inline mr-1" />
                Website
              </label>
              <a 
                href={formData.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline break-all"
              >
                {formData.website}
              </a>
            </div>
          )}

          {/* Current Location */}
          {formData.currentAddress && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <MapPinIcon className="h-4 w-4 inline mr-1" />
                Current Location
              </label>
              <div className="text-gray-900">{formData.currentAddress}</div>
              {formData.currentLatitude && formData.currentLongitude && (
                <div className="text-sm text-gray-500 mt-1">
                  {formData.currentLatitude.toFixed(6)}, {formData.currentLongitude.toFixed(6)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <form className="space-y-6">
        {/* Basic Information */}
        <BasicInfoFields
          formData={formData}
          validationErrors={validationErrors}
          onInputChange={handleInputChange}
        />

        {/* Contact Information */}
        <ContactInfoFields
          formData={formData}
          validationErrors={validationErrors}
          onInputChange={handleInputChange}
        />

        {/* Image Upload */}
        <ImageUploadSection
          imageUrl={formData.imageUrl}
          uploadingImage={uploadingImage}
          onImageUpload={handleImageUpload}
          onImageRemove={handleImageRemove}
        />

        {/* Location */}
        <LocationFields
          formData={formData}
          validationErrors={validationErrors}
          onInputChange={handleInputChange}
          showMapPicker={showMapPicker}
          setShowMapPicker={setShowMapPicker}
          mapPosition={mapPosition}
          setMapPosition={setMapPosition}
          handleLocationSelect={handleLocationSelect}
        />
      </form>
    </div>
  )
})

export default EntityDetailForm
